import { api } from "../base";

// ── Portfolios ──────────────────────────────────────────────────────────────

const entityEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /** Fetches all portfolios for an org. */
    getPortfolios: build.query<unknown[], string>({
      query: (orgId) => ({
        url: `/api/account/v1/orgs/${orgId}/portfolios`,
      }),
      providesTags: ["Entities"],
    }),

    /** Creates a portfolio. */
    createPortfolio: build.mutation<
      void,
      { orgId: string; displayName: string }
    >({
      query: ({ orgId, displayName }) => ({
        url: `/api/account/v1/orgs/${orgId}/portfolios`,
        method: "POST",
        body: { display_name: displayName },
      }),
      invalidatesTags: ["Entities"],
    }),

    /** Updates a portfolio's display name. */
    updatePortfolio: build.mutation<
      void,
      { orgId: string; portfolioId: string; displayName: string }
    >({
      query: ({ orgId, portfolioId, displayName }) => ({
        url: `/api/account/v1/orgs/${orgId}/portfolios/${portfolioId}`,
        method: "POST",
        body: { display_name: displayName },
      }),
      invalidatesTags: ["Entities"],
    }),

    /** Deletes a portfolio. */
    deletePortfolio: build.mutation<
      void,
      { orgId: string; portfolioId: string }
    >({
      query: ({ orgId, portfolioId }) => ({
        url: `/api/account/v1/orgs/${orgId}/portfolios/${portfolioId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Entities"],
    }),

    // ── Accounts ──────────────────────────────────────────────────────────

    /** Fetches venue accounts for a portfolio (lazy-loaded on row expand). */
    getAccounts: build.query<unknown[], string>({
      query: (portfolioId) => ({
        url: `/api/account/v1/portfolios/${portfolioId}/venue_accounts`,
      }),
      providesTags: (_result, _error, portfolioId) => [
        { type: "Accounts", id: portfolioId },
      ],
    }),

    /** Fetches a single venue account. */
    getAccount: build.query<
      Record<string, unknown>,
      { portfolioId: string; accountId: string }
    >({
      query: ({ portfolioId, accountId }) => ({
        url: `/api/account/v1/portfolios/${portfolioId}/venue_accounts/${accountId}`,
      }),
    }),

    /** Creates a venue account. */
    createAccount: build.mutation<
      void,
      { portfolioId: string; displayName: string }
    >({
      query: ({ portfolioId, displayName }) => ({
        url: `/api/account/v1/portfolios/${portfolioId}/venue_accounts`,
        method: "POST",
        body: { display_name: displayName },
      }),
      invalidatesTags: (_result, _error, { portfolioId }) => [
        { type: "Accounts", id: portfolioId },
      ],
    }),

    /** Updates a venue account. */
    updateAccount: build.mutation<
      void,
      {
        portfolioId: string;
        accountId: string;
        payload: Record<string, unknown>;
      }
    >({
      query: ({ portfolioId, accountId, payload }) => ({
        url: `/api/account/v1/portfolios/${portfolioId}/venue_accounts/${accountId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (_result, _error, { portfolioId }) => [
        { type: "Accounts", id: portfolioId },
      ],
    }),

    /** Deletes a venue account. */
    deleteAccount: build.mutation<
      void,
      { portfolioId: string; accountId: string }
    >({
      query: ({ portfolioId, accountId }) => ({
        url: `/api/account/v1/portfolios/${portfolioId}/venue_accounts/${accountId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portfolioId }) => [
        { type: "Accounts", id: portfolioId },
      ],
    }),

    // ── Dropcopy IPs ────────────────────────────────────────────────────

    /** Fetches dropcopy IP addresses. */
    getDropcopyIps: build.query<unknown[], void>({
      query: () => ({ url: "/api/account/v1/ips/dropcopy" }),
      transformResponse: (response: unknown[] | { data: unknown[] }) =>
        Array.isArray(response) ? response : (response.data ?? []),
    }),

    // ── Account Strategy Linking ────────────────────────────────────────

    /** Fetches the strategy linked to an account. */
    getAccountStrategy: build.query<unknown | null, string>({
      query: (accountId) => ({
        url: `/api/account/v1/venue_accounts/${accountId}/strategies`,
      }),
      providesTags: (_result, _error, accountId) => [
        { type: "AccountStrategy", id: accountId },
      ],
    }),

    /** Links a strategy to an account. */
    linkAccountStrategy: build.mutation<
      void,
      { accountId: string; strategyId: string }
    >({
      query: ({ accountId, strategyId }) => ({
        url: `/api/account/v1/venue_accounts/${accountId}/strategies`,
        method: "POST",
        body: { strategy_id: strategyId },
      }),
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: "AccountStrategy", id: accountId },
      ],
    }),

    /** Unlinks a strategy from an account. */
    unlinkAccountStrategy: build.mutation<
      void,
      { accountId: string; strategyId: string }
    >({
      query: ({ accountId, strategyId }) => ({
        url: `/api/account/v1/venue_accounts/${accountId}/strategies/${strategyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: "AccountStrategy", id: accountId },
      ],
    }),
  }),
});

export const {
  useGetPortfoliosQuery,
  useLazyGetPortfoliosQuery,
  useCreatePortfolioMutation,
  useUpdatePortfolioMutation,
  useDeletePortfolioMutation,
  useGetAccountsQuery,
  useLazyGetAccountsQuery,
  useGetAccountQuery,
  useLazyGetAccountQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useDeleteAccountMutation,
  useGetDropcopyIpsQuery,
  useGetAccountStrategyQuery,
  useLazyGetAccountStrategyQuery,
  useLinkAccountStrategyMutation,
  useUnlinkAccountStrategyMutation,
} = entityEndpoints;
